import clsx from "clsx";
import { Button } from "../../../../ui/button/button.tsx";
import { ThreeDots } from "react-loader-spinner";
import styles from "./action-banner.module.css";


type TimesheetState = "draft" | "submitted" | "pending";

interface ActionBannerProps {
  state: TimesheetState;
  /** When false, Save Draft and Submit are disabled (empty timesheet). */
  hasEntries?: boolean;
  /**
   * When the sheet is submitted but some rows are rejected and editable,
   * show Save / Submit so corrections can be persisted.
   */
  correctionMode?: boolean;
  onSaveDraft?: () => void;
  onOpenSubmit?: () => void;
  isSavingDraft?: boolean;
  isSubmitting?: boolean;
  hasModifiedEntries?: boolean;
}

const STATE_LABELS: Record<TimesheetState, string> = {
  pending: "Not Filled",
  draft: "Draft Saved",
  submitted: "Timesheet Submitted",
};

const RESUBMIT_LABEL = "Please resubmit your timesheet.";

const ActionBanner = ({
  state = "pending",
  hasEntries = true,
  correctionMode = false,
  onSaveDraft,
  onOpenSubmit,
  isSavingDraft = false,
  isSubmitting = false,
  hasModifiedEntries = false,
}: ActionBannerProps) => {
  const actionsDisabled =
    !hasEntries || isSavingDraft || isSubmitting;

  const submitDisabled =
    actionsDisabled || !hasModifiedEntries;
  const showActions = state !== "submitted" || correctionMode;
  const statusLabel =
    state === "submitted" && correctionMode
      ? RESUBMIT_LABEL
      : STATE_LABELS[state];

  const useResubmitStyle = state === "submitted" && correctionMode;

  return (
    <div
      className={clsx(
        styles.container,
        useResubmitStyle ? styles.needsResubmit : styles[state],
      )}
    >
      <div className={styles.statusInfo}>
        <span
          className={clsx(
            styles.indicator,
            useResubmitStyle ? styles.needsResubmit : styles[state],
          )}
        />
        <span className={styles.label}>{statusLabel}</span>
      </div>

      {showActions && (
        <div className={styles.actions}>
          {!correctionMode && (
            <Button
              variant="muted-outline"
              onClick={onSaveDraft}
              disabled={actionsDisabled}
              title={!hasEntries ? "Add at least one time entry" : undefined}
            >
              {isSavingDraft ? (
                <ThreeDots height="10" width="30" color="currentColor" />
              ) : (
                "Save Draft"
              )}
            </Button>
          )}
          <Button
            onClick={onOpenSubmit}
            disabled={submitDisabled}
            title={
              !hasEntries
                ? "Add at least one time entry"
                : !hasModifiedEntries
                  ? "Modify at least one entry before submitting"
                  : undefined
            }
          >
            {isSubmitting ? (
              <ThreeDots height="10" width="30" color="#fff" />
            ) : (
              "Submit Timesheet for Approval"
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ActionBanner;